import { useEffect } from "react";

import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog";
import { useUser } from "@/hooks/useUserProvider";
import { LogoText } from "../Logo/LogoText";
import { Information } from "./Information";
import { Background } from "./Background";
import { Login } from "./Login";
import { captureEvent } from "@/lib/analytics";

export const Authentication = ({
  resetPasswordToken,
}: {
  resetPasswordToken?: string;
}) => {
  const { isAuthenticationModalOpen, setAuthenticationModalOpen, accessToken } =
    useUser();

  const checkDefaultOpen = () => {
    if (localStorage.getItem("unideck.auth.dismissed") !== "true") {
      setAuthenticationModalOpen(true);
    }
  };

  const checkShouldOpen = async (accessToken: string | null, open: boolean) => {
    if (open && accessToken) {
      localStorage.setItem("unideck.auth.dismissed", "true");
      setAuthenticationModalOpen(false);
    }
  };

  useEffect(() => {
    checkDefaultOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkShouldOpen(accessToken, isAuthenticationModalOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, isAuthenticationModalOpen]);

  useEffect(() => {
    if (!isAuthenticationModalOpen) {
      return;
    }

    captureEvent("onboarding_auth_modal_opened", {
      source: resetPasswordToken ? "reset_password" : "default",
    });
  }, [isAuthenticationModalOpen, resetPasswordToken]);

  return (
    <>
      <ResponsiveDialog open={isAuthenticationModalOpen}>
        <ResponsiveDialogContent
          className="flex h-screen max-w-[100vw] flex-col items-center justify-start overflow-hidden rounded-none border-none px-0 pb-32 pt-8 outline-transparent ring-transparent sm:rounded-none lg:h-auto lg:max-w-[1100px] lg:flex-row lg:justify-center lg:rounded-2xl"
          overlayClassname="backdrop-blur"
          drawerClassName="h-[92dvh]"
          disableClose
        >
          <ResponsiveDialogHeader className="sr-only">
            <ResponsiveDialogTitle>Login</ResponsiveDialogTitle>
            <ResponsiveDialogDescription className="sr-only">
              Login to access more features.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <div className="relative flex w-full max-w-full flex-col lg:w-auto">
            <Background className="absolute left-[-35px] top-[-106px] -z-10 hidden lg:block" />
            <Background className="absolute left-[437px] top-[0px] -z-10 hidden rotate-180 lg:block" />
            <div className="mb-10 flex lg:mb-[75px]">
              <LogoText />
            </div>
            <div className="flex w-full max-w-[1024px] flex-col justify-center gap-4 lg:w-auto lg:flex-row lg:justify-normal lg:gap-10">
              <div className="z-10 hidden h-[384px] w-[700px] flex-shrink-0 flex-grow-0 flex-col lg:flex">
                <Information />
              </div>
              <div className="flex h-auto w-full flex-col items-center lg:h-[384px] lg:w-[414px]">
                <Login resetPasswordToken={resetPasswordToken} />
              </div>
            </div>
          </div>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </>
  );
};
